import React, { useEffect, useState } from "react";
import { GoogleMap, InfoWindow, LoadScript, Marker, Polyline } from "@react-google-maps/api";
import { useSearchParams } from "react-router-dom";
import { GetCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Button } from "@heroui/react";

// Approximate distance between two lat/lng in meters
function haversineDistance(p1, p2) {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Filter function
function filterClosePoints(path, minDistance = 10) {
  if (!path || path.length < 2) return path;

  const filtered = [path[0]];

  for (let i = 1; i < path.length; i++) {
    const prev = filtered[filtered.length - 1];
    const current = path[i];
    if (haversineDistance(prev, current) >= minDistance) {
      filtered.push(current);
    }
  }

  return filtered;
}

const GeoMapTimeline: React.FC<any> = (props) => {
  const [fromTime, setFromTime] = useState(moment().startOf("day"));
  const [toTime, setToTime] = useState(moment().endOf("day"));

  const [path, setPath] = useState<any[]>([]);
  const [markers, setMarkers] = useState([]);
  const [hovered, setHovered] = useState(null); // 🟢 you asked for this
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const [qParam] = useSearchParams();
  console.log(qParam.get("u"));

  const { error, isFetching, isError, isFetched, isLoading, refetch } = useQuery(
    ["_geo_user_points" + qParam.get("u")],
    () =>
      GetCommanWay(
        `/geo-map/api/0/locations?from=${fromTime.toISOString()}&to=${toTime.toISOString()}&format=geojsonpoi&user=${qParam.get(
          "u",
        )}&device=phone`,
        {},
        {},
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let tableData = [...(data?.data?.features || [])];

          const rawPath = [];
          const rawMarkers = [];

          tableData.forEach((t) => {
            const lat = t.geometry.coordinates[1];
            const lng = t.geometry.coordinates[0];
            const nameParts = t.properties.name?.split("|") || [];
            const isoTime = t.properties.isotst;

            const markerType = nameParts[0].trim();
            let specialData = nameParts[1]?.trim();
            let routePoint = false;

            if (nameParts[2]) {
              console.log(nameParts[2]);
              specialData = specialData ? specialData + "," + nameParts[2].trim() : nameParts[2].trim();
            }

            // Add point to path
            rawPath.push({ lat, lng });

            if (markerType !== "IN" && markerType !== "OUT" && !specialData) {
              routePoint = true;
            }

            // Decide marker icon and tooltip
            let icon = null;
            let tooltip = null;

            if (markerType === "IN") {
              icon = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
              tooltip = "Attendance IN";
            } else if (markerType === "OUT") {
              icon = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
              tooltip = "Attendance OUT";
            } else if (specialData) {
              icon = "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
              tooltip = `Important: ${specialData.split(",").join("<br/>")}`;
            } else if (routePoint) {
              icon = "route";
              tooltip = "-";
            }
            if (specialData) {
              const specialItems = specialData.split(",").map((x) => x.trim());
              rawMarkers.push({
                position: { lat, lng },
                icon,
                tooltip: "POD:", // or null
                specialItems, // ← pass separately
                time: isoTime,
              });
            } else {
              rawMarkers.push({
                position: { lat, lng },
                icon:
                  icon === "route"
                    ? {
                        path: 0,
                        fillColor: "#EC6135",
                        fillOpacity: 1,
                        strokeColor: "#0000FF",
                        strokeWeight: 1,
                        scale: 4,
                      }
                    : icon,
                tooltip: tooltip, // or null
                time: isoTime,
              });
            }
          });

          // Filter path
          const filteredPath = filterClosePoints(rawPath, 50);

          // Set start and end times
          if (tableData.length > 0) {
            setStartTime(tableData[0].properties.isotst);
            setEndTime(tableData[tableData.length - 1].properties.isotst);
          }

          // Optional: also filter markers based on path if needed
          setPath(filteredPath);
          setMarkers(rawMarkers); // <<-- you'll need to declare this state

          //setPath(filterClosePoints(finalData, 100)); // remove points closer than 10 meters

          //setPath(finalData);
        }
      },
    },
  );

  const [polylineOptions, setPolylineOptions] = useState<any>(null);

  const handleMapLoad = () => {
    if (window.google) {
      setMapLoaded(true);
      const arrowSymbol = {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 3,
        strokeColor: "#0000FF",
      };

      setPolylineOptions({
        strokeColor: "#0000FF",
        strokeOpacity: 0.6,
        strokeWeight: 4,
        icons: [
          {
            icon: arrowSymbol,
            offset: "50px",
            repeat: "500px",
          },
        ],
      });
    }
  };

  return (
    <>
      <div className="mx-4 my-2">
        <label>From: </label>
        <input type="datetime-local" value={fromTime.format("YYYY-MM-DDTHH:mm")} onChange={(e) => setFromTime(moment(e.target.value))} />

        <label style={{ marginLeft: "1rem" }}>To: </label>
        <input type="datetime-local" value={toTime.format("YYYY-MM-DDTHH:mm")} onChange={(e) => setToTime(moment(e.target.value))} />

        <Button
          type="button"
          className="ml-3 font-semibold"
          radius="sm"
          color="success"
          onPress={() => {
            refetch();
          }}
        >
          Search
        </Button>
      </div>

      {path.length == 0 ? (
        <p>Loading</p>
      ) : (
        <LoadScript googleMapsApiKey="AIzaSyCAGPfP2omanw5eBNpWLC3bNpMbW3ZazsY">
          <GoogleMap
            onLoad={handleMapLoad}
            mapContainerStyle={{
              width: "100%",
              height: "600px",
            }}
            center={path[0]}
            zoom={14}
            options={{
              gestureHandling: "greedy",
            }}
          >
            {/* Dots as Markers */}
            {false && path.map((point, index) => <Marker key={index} position={point} />)}

            {/* Green start marker */}
            {mapLoaded && (
              <Marker
                position={path[0]}
                icon={{
                  path: 0, // google.maps.SymbolPath.CIRCLE
                  fillColor: "#00FF00",
                  fillOpacity: 1,
                  strokeColor: "#0000FF",
                  strokeWeight: 2,
                  scale: 8,
                }}
                onClick={() => setHovered("start")}
              >
                {hovered === "start" && startTime && (
                  <InfoWindow position={path[0]} onCloseClick={() => setHovered(null)}>
                    <div>
                      <strong>Start Point</strong>
                      <br />
                      Time: {moment(startTime).format("YYYY-MM-DD HH:mm:ss")}
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            )}

            {/* Red end marker */}
            {mapLoaded && (
              <Marker
                position={path[path.length - 1]}
                icon={{
                  path: 0, // google.maps.SymbolPath.CIRCLE
                  fillColor: "#FF0000",
                  fillOpacity: 1,
                  strokeColor: "#0000FF",
                  strokeWeight: 2,
                  scale: 8,
                }}
                onClick={() => setHovered("end")}
              >
                {hovered === "end" && endTime && (
                  <InfoWindow position={path[path.length - 1]} onCloseClick={() => setHovered(null)}>
                    <div>
                      <strong>End Point</strong>
                      <br />
                      Time: {moment(endTime).format("YYYY-MM-DD HH:mm:ss")}
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            )}

            {markers.map((m, index) => (
              <Marker
                key={index}
                position={m.position}
                icon={m.icon || undefined}
                onClick={() => setHovered(index)}
                //onMouseOut={() => setHovered(null)}
              >
                {hovered === index &&
                  m.tooltip &&
                  (m.specialItems ? (
                    <InfoWindow position={m.position} onCloseClick={() => setHovered(null)}>
                      <div>
                        <p>POD:</p>
                        <ul>
                          {m.specialItems?.map((item, i) => (
                            <li key={i}>
                              <b>{item}</b>
                            </li>
                          ))}
                        </ul>
                        <div>Time: {moment(m.time).format("YYYY-MM-DD HH:mm:ss")}</div>
                      </div>
                    </InfoWindow>
                  ) : (
                    <InfoWindow position={m.position} onCloseClick={() => setHovered(null)}>
                      <div>
                        <strong>{m.tooltip}</strong>
                        <br />
                        Time: {moment(m.time).format("YYYY-MM-DD HH:mm:ss")}
                      </div>
                    </InfoWindow>
                  ))}
              </Marker>
            ))}

            {/* Line connecting dots */}
            <Polyline path={path} options={polylineOptions} />
          </GoogleMap>
        </LoadScript>
      )}
    </>
  );
};

export default GeoMapTimeline;
